import { useState } from 'react'
import Drawer from '../Drawer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addCampaignField, getCampaignFields, reorderCampaignFields, updateCampaignField } from '../api/endpoints'
import { ApiError } from '../api/client'
import type { FieldDef, FieldInput, FieldType } from '../api/types'
import { ErrorNotice } from '../components'

const TYPE_LABELS: Record<FieldType, string> = {
  TEXT: 'Short text', LONG_TEXT: 'Long text', NUMBER: 'Number', DATE: 'Date', PHONE: 'Phone number', EMAIL: 'Email', SELECT: 'Dropdown (one choice)', MULTI_SELECT: 'Tick boxes (several choices)', CHECKBOX: 'Yes / no tick box',
}
const CORE: Record<string, string> = { school: 'School', grade: 'Grade or class', board: 'Board', city: 'City', state: 'State' }

/** Common questions an admin can add with one click. Each can still be edited afterwards. */
export const SUGGESTIONS: FieldInput[] = [
  { key: 'parent_occupation', label: "Parent's occupation", helpText: null, type: 'TEXT', required: false, minorsOnly: true, options: [], group: 'Parent details', coreColumn: null },
  { key: 'parent_mobile', label: "Parent's mobile number", helpText: null, type: 'PHONE', required: false, minorsOnly: true, options: [], group: 'Parent details', coreColumn: null },
  { key: 'family_income', label: 'Annual family income', helpText: 'Used only for scholarship review', type: 'SELECT', required: false, minorsOnly: false, options: ['Under ₹5 lakh', '₹5 to 10 lakh', '₹10 to 25 lakh', 'Over ₹25 lakh'], group: 'Parent details', coreColumn: null },
  { key: 'subjects', label: 'Subjects you are studying', helpText: null, type: 'MULTI_SELECT', required: false, minorsOnly: false, options: ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'Economics', 'Commerce', 'Arts'], group: null, coreColumn: null },
  { key: 'languages', label: 'Languages you speak', helpText: null, type: 'MULTI_SELECT', required: false, minorsOnly: false, options: ['English', 'Hindi', 'Telugu', 'Other'], group: null, coreColumn: null },
  { key: 'heard_about_us', label: 'How did you hear about us?', helpText: null, type: 'SELECT', required: false, minorsOnly: false, options: ['School', 'Friend or family', 'Social media', 'Newspaper or TV', 'Other'], group: null, coreColumn: null },
  { key: 'declaration', label: 'I confirm the details I have given are correct', helpText: null, type: 'CHECKBOX', required: true, minorsOnly: false, options: [], group: null, coreColumn: null },
]

export function keyFromLabel(label: string): string {
  const k = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)
  return /^[a-z]/.test(k) ? k : k ? `q_${k}` : ''
}

export default function QuestionsEditor({ campaignId }: { campaignId: number }) {
  const queryClient = useQueryClient()
  const fields = useQuery({ queryKey: ['campaign-fields', campaignId], queryFn: () => getCampaignFields(campaignId) })
  const [editing, setEditing] = useState<FieldDef | 'new' | null>(null)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['campaign-fields', campaignId] })
  const quick = useMutation({ mutationFn: (f: FieldInput) => addCampaignField(campaignId, f), onSuccess: refresh })
  const move = useMutation({ mutationFn: (ids: number[]) => reorderCampaignFields(campaignId, ids), onSuccess: refresh })
  const toggle = useMutation({ mutationFn: (f: FieldDef) => updateCampaignField(campaignId, f.id, { label: f.label, active: !f.active }), onSuccess: refresh })

  const list = fields.data ?? []
  const taken = new Set(list.map((f) => f.key))
  const swap = (i: number, d: number) => {
    const ids = list.map((f) => f.id)
    ;[ids[i], ids[i + d]] = [ids[i + d], ids[i]]
    move.mutate(ids)
  }
  const groups = [...new Set(list.map((f) => f.group).filter((g): g is string => !!g))]

  return (
    <section className="card" aria-labelledby="q-title">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id="q-title">Registration questions</h2>
        <button type="button" className="primary" onClick={() => setEditing('new')}>Add a question</button>
      </div>
      <p className="muted">Name, mobile number and date of birth are always asked. Everything below is up to you. Switching a question off hides it but keeps what people already answered.</p>
      <div className="row" role="group" aria-label="Quick add" style={{ marginBottom: 12 }}>
        <span className="muted">Quick add:</span>
        {SUGGESTIONS.map((s) => <button key={s.key} type="button" disabled={taken.has(s.key) || quick.isPending} onClick={() => quick.mutate(s)}>+ {s.label}</button>)}
      </div>
      <ErrorNotice error={fields.error ?? quick.error ?? move.error ?? toggle.error} />
      <div className="table-wrap"><table>
        <thead><tr><th>Order</th><th>Question</th><th>Type</th><th>Who</th><th>Asked</th><th /></tr></thead>
        <tbody>
          {list.map((f, i) => (
            <tr key={f.id} style={f.active ? undefined : { opacity: 0.6 }}>
              <td><div className="row" style={{ gap: 2 }}>
                <button type="button" aria-label={`Move ${f.label} up`} disabled={i === 0 || move.isPending} onClick={() => swap(i, -1)}>↑</button>
                <button type="button" aria-label={`Move ${f.label} down`} disabled={i === list.length - 1 || move.isPending} onClick={() => swap(i, 1)}>↓</button></div></td>
              <td className="q-cell"><b>{f.label}</b>{f.group && <div className="muted">{f.group}</div>}{f.coreColumn && <div className="muted">Fills the standard “{CORE[f.coreColumn]}” column</div>}</td>
              <td>{TYPE_LABELS[f.type]}{f.options.length > 0 && <div className="muted" style={{ whiteSpace: 'normal', maxWidth: 260 }}>{f.options.join(', ')}</div>}</td>
              <td>{f.minorsOnly ? 'Under 18 only' : 'Everyone'}{f.required ? ' · required' : ' · optional'}</td>
              <td>{f.active ? <span className="badge ok">On</span> : <span className="badge">Off</span>}</td>
              <td><div className="row"><button type="button" onClick={() => setEditing(f)}>Edit</button>
                <button type="button" onClick={() => toggle.mutate(f)} disabled={toggle.isPending}>{f.active ? 'Switch off' : 'Switch on'}</button></div></td>
            </tr>
          ))}
        </tbody>
      </table></div>
      {editing && <FieldDialog campaignId={campaignId} field={editing === 'new' ? null : editing} groups={groups} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void refresh() }} />}
    </section>
  )
}

function FieldDialog({ campaignId, field, groups, onClose, onSaved }: { campaignId: number; field: FieldDef | null; groups: string[]; onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState(field?.label ?? '')
  const [key, setKey] = useState(field?.key ?? '')
  const [keyEdited, setKeyEdited] = useState(false)
  const [type, setType] = useState<FieldType>(field?.type ?? 'TEXT')
  const [options, setOptions] = useState((field?.options ?? []).join('\n'))
  const [helpText, setHelpText] = useState(field?.helpText ?? '')
  const [group, setGroup] = useState(field?.group ?? '')
  const [required, setRequired] = useState(field?.required ?? false)
  const [minorsOnly, setMinorsOnly] = useState(field?.minorsOnly ?? false)
  const [core, setCore] = useState(field?.coreColumn ?? '')
  const [active, setActive] = useState(field?.active ?? true)
  const save = useMutation({
    mutationFn: async () => {
      const body: FieldInput = {
        key, label, helpText: helpText.trim() || null, type, required, minorsOnly, options: options.split('\n').map((o) => o.trim()).filter(Boolean), group: group.trim() || null, coreColumn: core || null, active,
      }
      if (field) await updateCampaignField(campaignId, field.id, body)
      else await addCampaignField(campaignId, body)
    },
    onSuccess: onSaved,
  })
  const errs = save.error instanceof ApiError ? save.error.fields : {}
  const choices = type === 'SELECT' || type === 'MULTI_SELECT'
  const E = (k: string) => (errs[k] ? <p role="alert" className="muted" style={{ color: 'var(--danger)', margin: '2px 0 8px' }}>{errs[k]}</p> : null)
  return (
    <Drawer title={field ? 'Edit question' : 'Add a question'} onClose={onClose} onSubmit={() => save.mutate()}
      footer={<><button type="button" onClick={onClose}>Cancel</button><button type="submit" className="primary" disabled={save.isPending || !label.trim()}>{save.isPending ? 'Saving…' : 'Save question'}</button></>}>
        <label className="field">What do you want to ask?
          <input required value={label} onChange={(e) => { setLabel(e.target.value); if (!field && !keyEdited) setKey(keyFromLabel(e.target.value)) }} placeholder="e.g. Parent's occupation" />
        </label>
        {E("label")}
        {!field && (
          <label className="field">Short name used in downloads
            <input value={key} onChange={(e) => { setKey(e.target.value); setKeyEdited(true) }} />
          </label>
        )}
        {E("key")}
        <label className="field">Type of answer
          <select value={type} onChange={(e) => setType(e.target.value as FieldType)}>{Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        {E("type")}
        {choices && (
          <label className="field">Choices (one per line)
            <textarea rows={5} value={options} onChange={(e) => setOptions(e.target.value)} />
          </label>
        )}
        {E("options")}
        <label className="field">Help text (optional)<input value={helpText} onChange={(e) => setHelpText(e.target.value)} /></label>
        {E("helpText")}
        <label className="field">Heading to group it under (optional)
          <input list="groups" value={group} onChange={(e) => setGroup(e.target.value)} placeholder="e.g. Parent details" />
          <datalist id="groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
        </label>
        <div className="row" style={{ marginBottom: 12 }}>
          <label className="row"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Required</label>
          <label className="row"><input type="checkbox" checked={minorsOnly} onChange={(e) => setMinorsOnly(e.target.checked)} /> Ask only candidates under 18</label>
          {field && <label className="row"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Ask this question</label>}
        </div>
        {(type === 'TEXT' || type === 'SELECT') && (
          <label className="field">Also fill a standard column (optional)
            <select value={core} onChange={(e) => setCore(e.target.value)}><option value="">No</option>{Object.entries(CORE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </label>
        )}
        {E("coreColumn")}
        <ErrorNotice error={Object.keys(errs).length === 0 ? save.error : null} />
    </Drawer>
  )
}
